import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} EmpStockRefs
 * @property {HTMLElement} [pillContainer] - The element holding the rendered pill
 */

/**
 * The stock alert rendered under the price (blocks/emp-stock.liquid).
 *
 * The quantity, the fake-stock range and the tier all live in Liquid, so this
 * component never recomputes anything: on a variant update it takes the
 * re-rendered pill out of the response document and swaps its container, the
 * same way emp-descuento.js refreshes the discount pill.
 *
 * @extends {Component<EmpStockRefs>}
 */
class EmpStock extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section, dialog')?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Replaces the pill with the version rendered for the newly selected variant.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      // A picker for a different product fired this event. Leave our pill alone.
      return;
    }

    const newStock = event.detail.data.html.querySelector(`emp-stock[data-block-id="${this.dataset.blockId}"]`);
    if (!newStock) return;

    const newContainer = newStock.querySelector('[ref="pillContainer"]');
    const { pillContainer } = this.refs;
    if (newContainer && pillContainer) {
      pillContainer.replaceWith(newContainer);
    }

    // Variants with plenty of stock render an empty, hidden pill. Mirror the
    // server's decision both ways so the alert hides and reappears as the
    // shopper moves between variants.
    this.hidden = newStock.hasAttribute('hidden');
  };
}

if (!customElements.get('emp-stock')) {
  customElements.define('emp-stock', EmpStock);
}
